import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getCategoryNameBySlug } from '@/mocks/categories';
import { getComicById, getRelatedComics } from '@/mocks/comics';

const ComicDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicById(slug || '');
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到 - 汗汗漫画"
          description="抱歉，您要找的漫画不存在或已被移除。"
          canonicalUrl={`https://hanhanmanhua.com/comic/${slug}`}
        />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400">
                <i className="ri-file-unknow-line text-3xl"></i>
              </div>
              <h2 className="font-heading text-xl text-foreground-950 mb-2">漫画未找到</h2>
              <p className="text-sm text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedComics = getRelatedComics(comic, 6);
  const categoryName = getCategoryNameBySlug(comic.categorySlug);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 5);

  const seoTitle = `${comic.title} - 韩国漫画免费在线观看 | 汗汗漫画`;
  const seoDesc = `${comic.title}，作者${comic.author}，${comic.description.slice(0, 120)}。汗汗漫画提供${comic.title}免费在线观看，流畅下拉式阅读体验。`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: seoTitle,
    description: seoDesc,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updatedAt,
    dateModified: comic.updatedAt,
    url: `https://hanhanmanhua.com/comic/${comic.id}`,
    publisher: {
      '@type': 'Organization',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hanhanmanhua.com/comic/${comic.id}`,
    },
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanhanmanhua.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://hanhanmanhua.com/category/${comic.categorySlug}`,
      },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`${comic.title},${categoryName},韩国漫画,韩漫,免费在线观看,下拉式阅读,汗汗漫画`}
        canonicalUrl={`https://hanhanmanhua.com/comic/${comic.id}`}
        ogType="article"
        structuredData={{ ...structuredData, ...breadcrumbStructuredData }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>

          {/* Comic Detail Header */}
          <section className="py-8 md:py-12 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row gap-8">
                {/* Cover */}
                <div className="flex-shrink-0 w-full md:w-[280px]">
                  <div className="relative rounded-lg overflow-hidden bg-secondary-200" style={{ aspectRatio: '3/4' }}>
                    <img
                      src={comic.coverUrl}
                      alt={`${comic.title} 韩国漫画封面`}
                      title={`${comic.title} - 汗汗漫画韩国漫画免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                    <span
                      className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${
                        comic.status === '连载中'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}
                    >
                      {comic.status}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">
                    {comic.title}
                  </h1>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-4">
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line"></i>
                      </span>
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </span>
                      {comic.rating} 分
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </span>
                      {(comic.views / 10000).toFixed(1)}万 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-list-line"></i>
                      </span>
                      共 {comic.totalChapters} 话
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line"></i>
                      </span>
                      更新于 {comic.updatedAt}
                    </span>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                    {comic.description}
                  </p>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <Link
                      to={`/category/${comic.categorySlug}`}
                      className="px-6 py-2.5 bg-secondary-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-secondary-200 transition-all duration-300 whitespace-nowrap"
                    >
                      更多{categoryName}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Chapters List */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">
                章节列表
                <span className="text-sm text-foreground-500 font-sans ml-2">
                  共 {comic.totalChapters} 话
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {displayedChapters.map((ch) => (
                  <Link
                    key={ch.number}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-50 border border-secondary-200/60 rounded-lg hover:border-primary-300 transition-all duration-200 group"
                  >
                    <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                      {ch.updatedAt}
                    </span>
                  </Link>
                ))}
              </div>
              {comic.chapters.length > 5 && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="px-6 py-2.5 bg-background-50 border border-secondary-300 text-foreground-600 rounded-full text-sm hover:border-primary-300 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    {showAllChapters ? '收起' : `展开全部 ${comic.chapters.length} 话`}
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-10 md:py-14 bg-background-50">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl text-foreground-950">
                    相关推荐
                  </h2>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    查看更多
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedComics.map((rc) => (
                    <ComicCard key={rc.id} comic={rc} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ComicDetailPage;